// Functional labels for the existing approval card, matching Settings' terms.
import { namespaceCopyFor } from '@/i18n/ns';
import appApprovalsCopy, { type AppApprovalsCopyKey } from '@/i18n/ns/app-approvals';

const SETTINGS_ONLY_TYPES: Record<string, string> = {
  update_company_profile_settings: 'Change company profile',
  update_company_speech_settings: 'Change company speech policy',
  update_company_voice_settings: 'Change meeting voice identification',
  invite_company_member: 'Invite a colleague',
  update_company_member: 'Change colleague permissions',
  remove_company_member: 'Remove company access',
  transfer_company_ownership: 'Transfer company ownership',
  revoke_company_invitation: 'Revoke an invitation',
  update_item_sharing: 'Change sharing',
  update_work_connection: 'Change what this runs on',
  open_protected_canvas: 'Open a protected workspace flow',
  open_settings_flow: 'Open a secure Settings flow',
  update_memory: 'Edit personal memory',
};
export const SETTINGS_ACTION_FIELDS: Record<string, string> = {
  website: 'Website',
  market: 'Market',
  goal: 'Goal',
  revision: 'Revision',
  voiceMode: 'Voice mode',
  spokenApprovals: 'Spoken approvals',
  readAloud: 'Spoken replies',
  voicePassEnabled: 'Meeting voice pass',
  signaturesEnabled: 'Voice signatures',
  unusedRetentionDays: 'Unused signature retention (days)',
  purge: 'Permanently delete all voice signatures',
  enabled: 'Enabled',
  email: 'Email',
  role: 'Role',
  canPublish: 'May publish company work',
  expiresAt: 'Expires at',
  visibility: 'Who can access',
  recipients: 'Recipients',
  flow: 'Secure form',
  change: 'Change',
  connection: 'Connection',
  reads: 'Reads',
  resources: 'Resources',
  resourceLabels: 'Resources',
  allowChanges: 'May change data',
};
// A connection change card names the change in words, never its wire value.
export const WORK_CONNECTION_CHANGES: Record<string, string> = {
  attach: 'Connect',
  take_over: 'Use my own access',
  remove: 'Remove',
};

// The App cards' titles, in the person's language (the app-approvals namespace holds all 24).
const APP_TOOL_TYPE_KEYS: Record<string, AppApprovalsCopyKey> = {
  approve_app_outside_access: 'typeOutsideAccess',
  set_app_event: 'typeEvent',
  set_app_schedule: 'typeSchedule',
  set_app_data_writes: 'typeDataWrites',
  apply_app_table_change: 'typeTableChange',
  set_app_connected_write: 'typeConnectedWrite',
  confirm_app_code: 'typeConfirmCode',
  run_app_function_with_changes: 'typeRunChanges',
  publish_app_public_link: 'typePublicLink',
  unpublish_company_public_link: 'typeUnpublish',
};
const english = namespaceCopyFor(appApprovalsCopy, 'en');
export const SETTINGS_ACTION_TYPES: Record<string, string> = {
  ...SETTINGS_ONLY_TYPES,
  ...Object.fromEntries(
    Object.entries(APP_TOOL_TYPE_KEYS).map(([tool, key]) => [tool, english[key]]),
  ),
};
export function appActionTypes(locale: string): Record<string, string> {
  const words = namespaceCopyFor(appApprovalsCopy, locale);
  return Object.fromEntries(
    Object.entries(APP_TOOL_TYPE_KEYS).map(([tool, key]) => [tool, words[key]]),
  );
}

// Details labels that belong to one App card only. They never enter the shared field map: "kind" is the
// "Type" of a saved result elsewhere and "action" is a knowledge proposal's action.
const APP_TOOL_FIELD_KEYS: Record<string, Record<string, AppApprovalsCopyKey>> = {
  approve_app_outside_access: { hosts: 'labelHosts', access: 'labelAccess' },
  set_app_event: {
    event: 'labelEvent',
    kind: 'labelKind',
    collection: 'labelCollection',
    changes: 'labelChanges',
    runs: 'labelRuns',
    enabled: 'labelEnabled',
  },
  set_app_schedule: {
    job: 'labelJob',
    runs: 'labelFunction',
    schedule: 'labelSchedule',
    timezone: 'labelTimezone',
    state: 'labelEnabled',
  },
  set_app_data_writes: {
    function: 'labelFunction',
    collections: 'labelCollections',
    changes: 'labelChanges',
    who: 'labelWho',
    allowed: 'labelAllowed',
  },
  set_app_connected_write: {
    action: 'labelAction',
    destination: 'labelDestination',
    who: 'labelWho',
    enabled: 'labelEnabled',
  },
  confirm_app_code: { changedBy: 'labelChangedBy' },
  run_app_function_with_changes: { function: 'labelFunction', input: 'labelInput' },
  publish_app_public_link: {
    change: 'labelChange',
    publicTables: 'labelPublicTables',
    publicActions: 'labelPublicActions',
    publicRuns: 'labelPublicRuns',
    publicRunsOff: 'labelPublicRunsOff',
    embedSites: 'labelEmbedSites',
  },
  unpublish_company_public_link: { publicLink: 'labelPublicLink', publishedBy: 'labelPublishedBy' },
  apply_app_table_change: {
    change: 'labelChange',
    restores: 'labelRestores',
    table: 'labelTable',
    field: 'labelField',
    rows: 'labelRows',
    keptDays: 'labelKeptDays',
  },
};
export function appToolFields(locale: string): Record<string, Record<string, string>> {
  const words = namespaceCopyFor(appApprovalsCopy, locale);
  return Object.fromEntries(
    Object.entries(APP_TOOL_FIELD_KEYS).map(([tool, fields]) => [
      tool,
      Object.fromEntries(Object.entries(fields).map(([field, key]) => [field, words[key]])),
    ]),
  );
}
